import { FC, useEffect, useState } from 'react';

import { usePage } from '@inertiajs/react';
import { Filter, Heart } from 'lucide-react';

import { SupportDevelopersLinks } from '../../../../components/shared/SupportDevelopersLinks';
import { Button } from '../../../../components/ui/Button';
import {
    Sheet,
    SheetContent,
    SheetTrigger,
} from '../../../../components/ui/Sheet';
import { Expansion, ProductFilter, ProductType } from '../../../../types';
import { PropertyOption } from '../../../../types/shared';
import { expansionFilterOptions } from '../../../Products/filters/expansionFilterOptions';
import { PropertyFilterSelect } from '../../../Products/filters/PropertyFilterSelect';

type PageProps = {
    currentProductType: ProductType | null;
    filtersConfig: Record<string, ProductFilter[]>;
    expansions: Expansion[] | null;
};

interface Props {
    readonly filters: Record<string, string>;
    readonly onFilterChange: (filters: {
        q: Record<string, string>;
        expansionSlug?: string;
        preserveState?: boolean;
    }) => void;
    readonly expansion: Expansion | null;
    readonly extraFilters?: {
        key: string;
        label: string;
        allLabel: string;
        options: PropertyOption[];
        searchable?: boolean;
        multiselect?: boolean;
        testId?: string;
    }[];
    readonly hidePropertyFiltersWithoutProductType?: boolean;
    readonly immediateApplyKeys?: string[];
}

export const ProfileMobileFilters: FC<Props> = ({
    filters,
    onFilterChange,
    expansion,
    extraFilters,
    hidePropertyFiltersWithoutProductType = false,
    immediateApplyKeys = [],
}) => {
    const { currentProductType, filtersConfig, expansions } =
        usePage<PageProps>().props;
    const expansionOptions = expansionFilterOptions(expansions);

    const [open, setOpen] = useState(false);
    const [localFilters, setLocalFilters] = useState<{
        q: Record<string, string>;
        expansionSlug: string | undefined;
    }>({
        q: filters,
        expansionSlug: expansion?.slug,
    });

    const fallbackProductTypeSlug = Object.keys(filtersConfig)[0];
    const isRefreshingDynamicFilters = immediateApplyKeys.some(
        (key) => (localFilters.q[key] ?? 'all') !== (filters[key] ?? 'all'),
    );
    const currentFiltersConfig =
        (isRefreshingDynamicFilters
            ? []
            : hidePropertyFiltersWithoutProductType && !currentProductType
              ? []
              : currentProductType
                ? filtersConfig[currentProductType.slug]
                : filtersConfig[fallbackProductTypeSlug]) ?? [];

    useEffect(() => {
        setLocalFilters({
            q: filters,
            expansionSlug: expansion?.slug,
        });
    }, [expansion?.slug, filters]);

    const handleExpansionChange = (expansionSlug: string) => {
        setLocalFilters((current) => ({
            q: current.q,
            expansionSlug,
        }));
    };

    const handleChange = (propertyId: string, optionId: string) => {
        setLocalFilters((current) => {
            const updatedFilters = {
                q: {
                    ...current.q,
                    [propertyId]: optionId,
                },
                expansionSlug: current.expansionSlug ?? expansion?.slug,
            };

            if (immediateApplyKeys.includes(propertyId)) {
                onFilterChange({
                    ...updatedFilters,
                    preserveState: true,
                });
            }

            return updatedFilters;
        });
    };

    const handleApplyFilters = () => {
        setOpen(false);
        onFilterChange(localFilters);
    };

    return (
        <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                    <Button
                        variant="default"
                        size="icon"
                        className="fixed right-6 bottom-20 z-50 h-12 w-12 rounded-xl shadow-lg"
                        data-testid="mobile-filters-trigger"
                    >
                        <Filter className="h-5 w-5" />
                        <span className="sr-only">Фільтри</span>
                    </Button>
                </SheetTrigger>
                <SheetContent
                    title="Фільтри"
                    side="bottom"
                    className="h-[100dvh] overflow-hidden rounded-none p-0"
                >
                    <div className="flex h-full flex-col">
                        <div className="px-4 pt-4">
                            <h3 className="mb-4 text-center text-lg font-medium">
                                Фільтри
                            </h3>
                        </div>
                        <div className="flex-1 space-y-5 overflow-y-auto px-4 pb-4">
                            {extraFilters?.map((filter) => (
                                <div key={filter.key}>
                                    <label className="mb-2 block text-sm font-medium">
                                        {filter.label}
                                    </label>
                                    <PropertyFilterSelect
                                        value={
                                            localFilters.q[filter.key] || 'all'
                                        }
                                        onChange={(val) =>
                                            handleChange(filter.key, val)
                                        }
                                        options={filter.options}
                                        searchable={filter.searchable}
                                        multiselect={filter.multiselect}
                                        allLabel={filter.allLabel}
                                        testId={filter.testId}
                                    />
                                </div>
                            ))}

                            {isRefreshingDynamicFilters && (
                                <p className="text-sm text-muted-foreground">
                                    Оновлюємо доступні фільтри...
                                </p>
                            )}

                            {!(
                                isRefreshingDynamicFilters ||
                                expansionOptions.length === 0 ||
                                currentProductType?.slug === 'accessories' ||
                                currentProductType?.slug === 'sealed'
                            ) && (
                                <div>
                                    <label className="mb-2 block text-sm font-medium">
                                        Колекції
                                    </label>
                                    <PropertyFilterSelect
                                        value={
                                            localFilters.expansionSlug || 'all'
                                        }
                                        onChange={(val) =>
                                            handleExpansionChange(val)
                                        }
                                        options={expansionOptions}
                                        searchable
                                        showSelectionIndicator={false}
                                        allLabel="Усі колекції"
                                        searchPlaceholder="Пошук колекцій..."
                                        emptyText="Колекції не знайдено"
                                        testId="expansion-filter"
                                    />
                                </div>
                            )}
                            {currentFiltersConfig?.map((product_property) => (
                                <div key={product_property.id}>
                                    <label className="mb-2 block text-sm font-medium">
                                        {product_property.name}
                                    </label>
                                    <PropertyFilterSelect
                                        value={
                                            localFilters.q[
                                                product_property.slug
                                            ] || 'all'
                                        }
                                        onChange={(val) =>
                                            handleChange(
                                                product_property.slug,
                                                val,
                                            )
                                        }
                                        options={
                                            product_property.product_property_options ??
                                            []
                                        }
                                        searchable={product_property.searchable}
                                        multiselect={
                                            product_property.multiselect
                                        }
                                        testId={`property-filter-${product_property.slug}`}
                                    />
                                </div>
                            ))}
                            <div className="rounded-lg border bg-muted/30 p-3">
                                <h4 className="mb-2 flex items-center text-sm font-medium">
                                    <Heart className="mr-2 h-4 w-4 text-red-500" />
                                    Підтримати проєкт
                                </h4>
                                <p className="mb-2 text-xs text-muted-foreground">
                                    Допоможіть нам розвивати CartOn.
                                </p>
                                <SupportDevelopersLinks />
                            </div>
                        </div>
                        <div className="space-y-3 border-t px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
                            <Button
                                className="w-full"
                                onClick={handleApplyFilters}
                                disabled={isRefreshingDynamicFilters}
                            >
                                Застосувати фільтри
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => setOpen(false)}
                            >
                                Сховати фільтри
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
};
